import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  signal,
} from '@angular/core';

import { NgpAvatar, NgpAvatarFallback, NgpAvatarImage } from 'ng-primitives/avatar';
import { twMerge } from 'tailwind-merge';

import { IconComponent, type IconSize } from '@shared/icon';

import { avatarCva, imageCva } from './avatar.cva';
import { type AvatarLoadType, type AvatarSize } from './avatar.types';

@Component({
  selector: 'ml-avatar',
  imports: [NgpAvatarImage, NgpAvatarFallback, IconComponent],
  hostDirectives: [NgpAvatar],
  host: {
    role: 'img',
    '[attr.aria-label]': 'ariaLabel()',
    '[attr.title]': 'ariaLabel()',
    '[class]': 'hostClass()',
  },
  templateUrl: './avatar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AvatarComponent {
  readonly image = input<string>();
  readonly fallback = input<string>('');
  readonly ariaLabel = input.required<string>();
  readonly fallbackDelay = input<number>(0);
  readonly loadType = input<AvatarLoadType>('lazy');
  readonly size = input<AvatarSize>('sm');
  readonly rounded = input(false, { transform: booleanAttribute });
  readonly extraClass = input<string>('', { alias: 'class' });
  protected readonly avatarToIconSizeMap: Record<AvatarSize, IconSize> = {
    sm: 'lg',
    '2xl': '2xl',
  };
  protected readonly imageClass = imageCva;
  protected readonly defaultImage = signal(false);
  protected readonly hostClass = computed(() =>
    twMerge(avatarCva({ size: this.size(), rounded: this.rounded() }), this.extraClass()),
  );

  protected readonly iconSize = computed(() => this.avatarToIconSizeMap[this.size()]);

  protected onImageError(): void {
    this.defaultImage.set(true);
  }

  protected onImageLoad(): void {
    this.defaultImage.set(false);
  }
}
